import flightsData from '../data/flights.json';
import citiesData from '../data/cities.json';

export const searchFlights = (searchParams) => {
  const { fromCode, toCode, departDate, tripType } = searchParams;
  
  if (!fromCode || !toCode) {
    return [];
  }

  // Get nearby airports for from and to cities
  const fromCity = getCityByCode(fromCode);
  const toCity = getCityByCode(toCode);
  
  const fromAirports = [fromCode];
  const toAirports = [toCode];
  
  // Add nearby airports if they exist
  if (fromCity && fromCity.nearbyAirports) {
    fromAirports.push(...fromCity.nearbyAirports);
  }
  
  if (toCity && toCity.nearbyAirports) {
    toAirports.push(...toCity.nearbyAirports);
  }

  // Filter flights based on departure and arrival codes (including nearby airports)
  const filteredFlights = flightsData.filter(flight => {
    // For round trip, we need to find flights that match the route or return route
    if (tripType === 'roundTrip') {
      return (fromAirports.includes(flight.departureCode) && toAirports.includes(flight.arrivalCode)) ||
             (toAirports.includes(flight.departureCode) && fromAirports.includes(flight.arrivalCode));
    }
    
    // For one way, only match the exact route
    return fromAirports.includes(flight.departureCode) && toAirports.includes(flight.arrivalCode);
  });

  // Apply date-wise pricing if departDate is provided
  const processedFlights = filteredFlights.map(flight => {
    if (departDate && flight.pricing) {
      const dateKey = new Date(departDate).toISOString().split('T')[0];
      const pricing = flight.pricing[dateKey];
      
      if (pricing) {
        return {
          ...flight,
          mainPrice: pricing.mainPrice,
          premiumPrice: pricing.premiumPrice,
          // Update availability based on pricing
          mainAvailable: pricing.mainPrice !== null,
          premiumAvailable: pricing.premiumPrice !== null
        };
      }
    }
    
    // Fallback to default pricing (use first available date)
    if (flight.pricing) {
      const firstDateKey = Object.keys(flight.pricing)[0];
      const defaultPricing = flight.pricing[firstDateKey];
      return {
        ...flight,
        mainPrice: defaultPricing.mainPrice,
        premiumPrice: defaultPricing.premiumPrice,
        mainAvailable: defaultPricing.mainPrice !== null,
        premiumAvailable: defaultPricing.premiumPrice !== null
      };
    }
    
    return flight;
  });

  // Optional: Log for debugging
  // console.log('Search params:', { fromCode, toCode, tripType, departDate });
  // console.log('From airports:', fromAirports);
  // console.log('To airports:', toAirports);
  // console.log('Found flights:', processedFlights.length);

  return processedFlights;
};

export const getCityByCode = (code) => {
  return citiesData.find(city => city.code === code);
};

export const getCityNameByCode = (code) => {
  const city = getCityByCode(code);
  return city ? city.city : code;
};

export const formatCityDisplay = (code) => {
  const city = getCityByCode(code);
  return city ? `${city.city}, ${city.stateCode || city.country}` : code;
};

export const getAirportName = (code) => {
  const city = getCityByCode(code);
  return city ? city.airport : code;
};

export const getAvailableDates = (fromCode, toCode) => {
  const flights = flightsData.filter(flight => 
    (flight.departureCode === fromCode && flight.arrivalCode === toCode) ||
    (flight.departureCode === toCode && flight.arrivalCode === fromCode)
  );
  
  const allDates = new Set();
  flights.forEach(flight => {
    if (flight.pricing) {
      Object.keys(flight.pricing).forEach(date => allDates.add(date));
    }
  });
  
  return Array.from(allDates).sort();
};

export const updateFlightPricesForDate = (flights, selectedDate) => {
  if (!selectedDate) return flights;
  
  const dateKey = new Date(selectedDate).toISOString().split('T')[0];
  
  return flights.map(flight => {
    if (flight.pricing && flight.pricing[dateKey]) {
      const pricing = flight.pricing[dateKey];
      return {
        ...flight,
        mainPrice: pricing.mainPrice,
        premiumPrice: pricing.premiumPrice,
        mainAvailable: pricing.mainPrice !== null,
        premiumAvailable: pricing.premiumPrice !== null
      };
    }
    
    // If no pricing for selected date, keep original flight data
    return flight;
  });
};

export const getNearbyAirports = (cityCode) => {
  const city = getCityByCode(cityCode);
  if (!city) return [];
  
  const airports = [city]; // Include the main airport
  
  // Add nearby airports if they exist
  if (city.nearbyAirports && city.nearbyAirports.length > 0) {
    const nearbyAirportDetails = city.nearbyAirports
      .map(code => getCityByCode(code))
      .filter(Boolean); // Remove any null/undefined entries
    
    airports.push(...nearbyAirportDetails);
  }
  
  return airports;
};

export const getAirportDisplayName = (airport) => {
  return `${airport.code} - ${airport.airport}`;
};

export const getCityDisplayForNearbyAirports = (cityCode) => {
  const city = getCityByCode(cityCode);
  if (!city) return 'Unknown City';
  
  return `${city.city}, ${city.state || city.country}`;
};

export const isDomesticUSARoute = (fromCode, toCode) => {
  const fromCity = getCityByCode(fromCode);
  const toCity = getCityByCode(toCode);
  
  // Check if both cities are in the USA
  return fromCity && toCity && 
         fromCity.country === 'USA' && 
         toCity.country === 'USA';
};

export const getAvailableAirlines = (fromCode, toCode) => {
  const isDomestic = isDomesticUSARoute(fromCode, toCode);
  
  if (isDomestic) {
    // For domestic USA routes, only American Airlines
    return ['American Airlines'];
  } else {
    // For international routes, show multiple airlines
    return ['American Airlines', 'British Airways', 'IndiGo', 'Delta Airlines', 'United Airlines'];
  }
};

export const getAvailableStopOptions = (searchResults) => {
  if (!searchResults || searchResults.length === 0) {
    return ['Any number of stops'];
  }

  // Get unique stop types from search results
  const availableStops = [...new Set(searchResults.map(flight => flight.stops))];
  
  // Define all possible options in order
  const allOptions = [
    'Nonstop only',
    '1 stop or fewer', 
    '2 stops or fewer',
    'Any number of stops'
  ];
  
  // Determine which options should be available based on actual flight data
  const availableOptions = [];
  
  // Always include "Any number of stops" as default
  availableOptions.push('Any number of stops');
  
  // Check if we have nonstop flights
  if (availableStops.includes('Nonstop')) {
    availableOptions.unshift('Nonstop only');
  }
  
  // Check if we have 1 stop flights  
  if (availableStops.includes('1 stop')) {
    if (!availableOptions.includes('Nonstop only')) {
      availableOptions.unshift('Nonstop only'); // Include even if no nonstop for consistency
    }
    if (!availableOptions.includes('1 stop or fewer')) {
      availableOptions.splice(-1, 0, '1 stop or fewer'); // Insert before "Any number of stops"
    }
  }
  
  // Check if we have 2+ stop flights
  if (availableStops.includes('2+ stops') || availableStops.includes('2 stops')) {
    if (!availableOptions.includes('Nonstop only')) {
      availableOptions.unshift('Nonstop only');
    }
    if (!availableOptions.includes('1 stop or fewer')) {
      availableOptions.splice(-1, 0, '1 stop or fewer');
    }
    if (!availableOptions.includes('2 stops or fewer')) {
      availableOptions.splice(-1, 0, '2 stops or fewer');
    }
  }
  
  return availableOptions;
};